import { useState, useCallback, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import { cn } from '@/lib/utils'
import { api } from '@/lib/api'
import type { ApiResponse } from '@/types/api'
import type { Application, ApplicationDecision, ApplicationPriority, Document, Requirement } from '@/types/models'
import RequirementsPanel from '@/components/requirements/RequirementsPanel'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type TabKey = 'requirements' | 'documents' | 'recommenders'

interface Props {
  application: Application | null
  workspaceId: string
  cycleId: string | null
  onClose: () => void
}

// ---------------------------------------------------------------------------
// Debounce utility
// ---------------------------------------------------------------------------

/**
 * Returns a debounced version of `fn` that delays invocation by `ms` ms.
 */
function useDebouncedCallback<T extends unknown[]>(
  fn: (...args: T) => void,
  ms: number,
): (...args: T) => void {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  return useCallback(
    (...args: T) => {
      if (timer.current) clearTimeout(timer.current)
      timer.current = setTimeout(() => fn(...args), ms)
    },
    [fn, ms],
  )
}

// ---------------------------------------------------------------------------
// ApplicationDrawer
// ---------------------------------------------------------------------------

/**
 * ApplicationDrawer — right-side sheet for viewing/editing an application.
 *
 * Inline edit with debounced PATCH for: deadline, priority, notes,
 * applicationUrl, decision, decisionNotes.
 *
 * Tabs: Requirements | Documents | Recommenders
 */
export default function ApplicationDrawer({ application, workspaceId, cycleId, onClose }: Props) {
  const queryClient = useQueryClient()
  const [activeTab, setActiveTab] = useState<TabKey>('requirements')

  const patchMutation = useMutation({
    mutationFn: (data: Partial<Application>) =>
      api.patch<ApiResponse<Application>>(`/v1/applications/${application?.id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['applications', workspaceId, cycleId] })
    },
  })

  const debouncedPatch = useDebouncedCallback(
    (data: Partial<Application>) => {
      if (!application) return
      patchMutation.mutate(data)
    },
    600,
  )

  // Documents query
  const { data: docsData } = useQuery<ApiResponse<Document[]>>({
    queryKey: ['documents', workspaceId],
    queryFn: () => api.get<ApiResponse<Document[]>>('/v1/documents'),
    enabled: activeTab === 'documents' && !!workspaceId,
  })

  // Recommenders tab — requirements filtered to LOR type
  const { data: reqData } = useQuery<ApiResponse<Requirement[]>>({
    queryKey: ['requirements', application?.id ?? ''],
    queryFn: () =>
      api.get<ApiResponse<Requirement[]>>(
        `/v1/requirements?applicationId=${application?.id}`,
      ),
    enabled: activeTab === 'recommenders' && !!application?.id,
  })

  const lorRequirements = (reqData?.data ?? []).filter((r) => r.type === 'LOR')

  if (!application) return null

  const TABS: { key: TabKey; label: string }[] = [
    { key: 'requirements', label: 'Requirements' },
    { key: 'documents', label: 'Documents' },
    { key: 'recommenders', label: 'Recommenders' },
  ]

  return (
    <Dialog.Root open={!!application} onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-40 bg-black/30" />
        <Dialog.Content
          className="fixed right-0 top-0 z-50 flex h-full w-full max-w-xl flex-col border-l bg-background shadow-xl focus:outline-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right"
          aria-label={`Edit application: ${application.universityName}`}
        >
          {/* Header */}
          <div className="flex items-start justify-between border-b px-5 py-4">
            <div className="min-w-0 flex-1">
              <Dialog.Title className="text-base font-semibold text-foreground truncate">
                {application.universityName}
              </Dialog.Title>
              <p className="text-sm text-muted-foreground truncate">{application.programName}</p>
            </div>
            <Dialog.Close asChild>
              <button
                type="button"
                className="ml-3 rounded-md p-1 text-muted-foreground hover:bg-accent focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                aria-label="Close drawer"
              >
                <X className="h-5 w-5" />
              </button>
            </Dialog.Close>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {/* Editable fields */}
            <div className="grid gap-3 sm:grid-cols-2">
              {/* Deadline */}
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Deadline</span>
                <input
                  type="date"
                  defaultValue={application.deadline?.slice(0, 10) ?? ''}
                  onChange={(e) => debouncedPatch({ deadline: e.target.value || undefined })}
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>

              {/* Priority */}
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Priority</span>
                <select
                  defaultValue={application.priority}
                  onChange={(e) =>
                    patchMutation.mutate({ priority: e.target.value as ApplicationPriority })
                  }
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </label>

              {/* Application URL */}
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Application URL</span>
                <input
                  type="url"
                  defaultValue={application.applicationUrl ?? ''}
                  placeholder="https://..."
                  onChange={(e) => debouncedPatch({ applicationUrl: e.target.value || undefined })}
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>

              {/* Decision */}
              <label className="flex flex-col gap-1">
                <span className="text-xs font-medium text-muted-foreground">Decision</span>
                <select
                  defaultValue={application.decision ?? ''}
                  onChange={(e) =>
                    patchMutation.mutate({
                      decision: (e.target.value as ApplicationDecision) || undefined,
                    })
                  }
                  className="rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <option value="">— None —</option>
                  <option value="ADMITTED">Admitted</option>
                  <option value="WAITLISTED">Waitlisted</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="DEFERRED">Deferred</option>
                </select>
              </label>

              {/* Notes */}
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Notes</span>
                <textarea
                  defaultValue={application.notes ?? ''}
                  rows={3}
                  onChange={(e) => debouncedPatch({ notes: e.target.value })}
                  className="resize-none rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>

              {/* Decision notes */}
              <label className="flex flex-col gap-1 sm:col-span-2">
                <span className="text-xs font-medium text-muted-foreground">Decision Notes</span>
                <textarea
                  defaultValue={application.decisionNotes ?? ''}
                  rows={2}
                  onChange={(e) => debouncedPatch({ decisionNotes: e.target.value })}
                  className="resize-none rounded border bg-background px-3 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </label>
            </div>

            {/* Tabs */}
            <div>
              <div className="flex border-b">
                {TABS.map((tab) => (
                  <button
                    key={tab.key}
                    type="button"
                    onClick={() => setActiveTab(tab.key)}
                    className={cn(
                      'px-4 py-2 text-sm font-medium transition-colors focus:outline-none',
                      activeTab === tab.key
                        ? 'border-b-2 border-brand-blue text-brand-blue'
                        : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="pt-4">
                {activeTab === 'requirements' && (
                  <RequirementsPanel applicationId={application.id} />
                )}

                {activeTab === 'documents' && (
                  <div className="space-y-2">
                    {(docsData?.data ?? []).length === 0 && (
                      <p className="text-sm text-muted-foreground">No documents attached yet.</p>
                    )}
                    {(docsData?.data ?? []).map((doc) => (
                      <div
                        key={doc.id}
                        className="flex items-center justify-between rounded-md border bg-card px-3 py-2"
                      >
                        <div>
                          <p className="text-sm font-medium text-foreground">{doc.name}</p>
                          <p className="text-xs text-muted-foreground">{doc.type}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {activeTab === 'recommenders' && (
                  <div className="space-y-2">
                    {lorRequirements.length === 0 && (
                      <p className="text-sm text-muted-foreground">
                        No LOR requirements found. Add them in the Requirements tab.
                      </p>
                    )}
                    {lorRequirements.map((req) => (
                      <div
                        key={req.id}
                        className="rounded-md border bg-card px-3 py-2"
                      >
                        <p className="text-sm font-medium text-foreground">{req.title}</p>
                        {req.recommenderName && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {req.recommenderName}
                          </p>
                        )}
                        {req.recommendationStatus && (
                          <span className="mt-1 inline-block rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
                            {req.recommendationStatus.replace('_', ' ')}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
